import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  interpolateColors,
  spring,
} from 'remotion';

// 气候变化数据 (1900-2024)
const climateData = [
  { year: 1900, tempAnomaly: -0.2, co2: 295, iceExtent: 12.5 },
  { year: 1920, tempAnomaly: -0.1, co2: 303, iceExtent: 12.3 },
  { year: 1940, tempAnomaly: 0.1, co2: 310, iceExtent: 12.0 },
  { year: 1960, tempAnomaly: 0.0, co2: 317, iceExtent: 11.8 },
  { year: 1980, tempAnomaly: 0.3, co2: 338, iceExtent: 11.2 },
  { year: 2000, tempAnomaly: 0.6, co2: 369, iceExtent: 10.5 },
  { year: 2020, tempAnomaly: 1.2, co2: 414, iceExtent: 9.8 },
  { year: 2024, tempAnomaly: 1.4, co2: 422, iceExtent: 9.5 },
];

// 极端天气事件数据
const extremeWeatherEvents = [
  { type: '飓风', icon: '🌀', count: 289, trend: '+23%' },
  { type: '洪水', icon: '🌊', count: 4321, trend: '+34%' },
  { type: '野火', icon: '🔥', count: 1567, trend: '+45%' },
  { type: '干旱', icon: '🏜️', count: 892, trend: '+28%' },
];

// 温度计组件
const Thermometer: React.FC<{ value: number; maxValue: number; frame: number; fps: number }> = ({
  value,
  maxValue,
  frame,
  fps,
}) => {
  const progress = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const height = interpolate(value * progress, [0, maxValue], [0, 300]);
  const color = interpolateColors(value, [0, 0.5, 1, 1.5], ['#4ECDC4', '#FFE66D', '#FF6B6B', '#C73E1D']);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          width: 60,
          height: 320,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 30,
          position: 'relative',
          overflow: 'hidden',
          border: '3px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: height,
            background: `linear-gradient(to top, ${color}, #FFE66D)`,
            borderRadius: '0 0 27px 27px',
            transition: 'height 0.3s ease',
          }}
        />
      </div>
      <div
        style={{
          width: 80,
          height: 80,
          background: 'radial-gradient(circle, #FF6B6B, #C73E1D)',
          borderRadius: '50%',
          marginTop: -15,
          border: '3px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          color: 'white',
          fontWeight: 'bold',
        }}
      >
        {value.toFixed(1)}°
      </div>
    </div>
  );
};

// CO2 柱状图组件
const CO2BarChart: React.FC<{ data: typeof climateData; frame: number; fps: number }> = ({
  data,
  frame,
  fps,
}) => {
  const visibleData = data.filter((_, i) => {
    const delay = i * 15;
    return frame >= delay;
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        width: 700,
        height: 250,
        padding: '0 20px',
      }}
    >
      {visibleData.map((point, index) => {
        const delay = index * 15;
        const progress = spring({
          frame: frame - delay,
          fps,
          config: { damping: 12, stiffness: 200 },
        });

        const barHeight = interpolate(point.co2, [290, 425], [40, 220]);
        const opacity = interpolate(progress, [0, 1], [0, 1]);

        return (
          <div key={point.year} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 60,
                height: barHeight * progress,
                background: `linear-gradient(to top, #FF8C00, #FFD700)`,
                borderRadius: '8px 8px 0 0',
                opacity,
                boxShadow: '0 0 20px rgba(255, 140, 0, 0.4)',
              }}
            />
            <div
              style={{
                marginTop: 8,
                fontSize: 20,
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 'bold',
                opacity,
              }}
            >
              {point.co2}
            </div>
            <div style={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)', marginTop: 4, opacity }}>
              {point.year}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 冰盖融化动画组件
const IceCaps: React.FC<{ iceExtent: number; frame: number; fps: number }> = ({
  iceExtent,
  frame,
  fps,
}) => {
  const meltProgress = interpolate(iceExtent, [9.5, 12.5], [1, 0]);
  const progress = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const remainingIce = 1 - meltProgress * progress;

  return (
    <div style={{ position: 'relative', width: 300, height: 250 }}>
      {/* 地球 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 200,
          height: 200,
          background: 'radial-gradient(circle at 30% 30%, #4A90E2, #1565C0)',
          borderRadius: '50%',
          boxShadow: '0 0 40px rgba(74, 144, 226, 0.5)',
        }}
      />
      
      {/* 北极冰盖 */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 140 * remainingIce,
          height: 60 * remainingIce,
          background: 'radial-gradient(ellipse, #E3F2FD, #90CAF9)',
          borderRadius: '50%',
          opacity: remainingIce,
          boxShadow: '0 0 20px rgba(255, 255, 255, 0.6)',
        }}
      />
      
      {/* 水滴 */}
      {meltProgress > 0.3 && (
        <>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: 100 + i * 25,
                top: 80 + (frame % 60) * 2,
                fontSize: 24,
                opacity: 0.8 - (frame % 60) * 0.013,
              }}
            >
              💧
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// 极端天气事件卡片
const WeatherEventCard: React.FC<{
  event: typeof extremeWeatherEvents[0];
  index: number;
  frame: number;
  fps: number;
}> = ({ event, index, frame, fps }) => {
  const delay = 150 + index * 20;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const scale = interpolate(progress, [0, 1], [0.5, 1]);
  const opacity = progress;

  if (frame < delay) return null;

  return (
    <div
      style={{
        width: 200,
        padding: '20px',
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 16,
        border: '1px solid rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        opacity,
        transform: `scale(${scale})`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 48 }}>{event.icon}</span>
        <span style={{ fontSize: 28, fontWeight: 'bold', color: 'white' }}>{event.type}</span>
      </div>
      <div style={{ fontSize: 36, fontWeight: 'bold', color: '#FF6B6B', marginBottom: 4 }}>
        {event.count.toLocaleString()}
      </div>
      <div style={{ fontSize: 22, color: '#FF8C00', fontWeight: '600' }}>{event.trend}</div>
    </div>
  );
};

// 行动号召组件
const CallToAction: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = spring({
    frame: frame - 250,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const scale = interpolate(progress, [0, 1], [0.8, 1]);
  const opacity = progress;

  if (frame < 250) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 60,
        left: 80,
        right: 80,
        padding: '26px 60px',
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(33, 150, 243, 0.9))',
        borderRadius: 20,
        opacity,
        transform: `scale(${scale})`,
        boxShadow: '0 10px 40px rgba(76, 175, 80, 0.3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 42, fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap' }}>
          🌍 我们可以改变未来
        </div>
        <div style={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.9)', whiteSpace: 'nowrap' }}>
          减少碳排放 • 使用清洁能源 • 保护森林 • 可持续生活
        </div>
      </div>
    </div>
  );
};

// 主组件
export const ClimateChangeVisualization: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const currentDataPoint = climateData[Math.min(Math.floor(frame / 45), climateData.length - 1)];
  
  const titleOpacity = interpolate(frame, [0, 30], [0, 1]);
  const subtitleOpacity = interpolate(frame, [30, 60], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0A1929 0%, #1A237E 100%)',
        color: 'white',
        fontFamily: 'system-ui',
        padding: 40,
      }}
    >
      {/* 标题 */}
      <div style={{ marginBottom: 60 }}>
        <h1
          style={{
            fontSize: 72,
            fontWeight: 'bold',
            margin: 0,
            marginBottom: 20,
            opacity: titleOpacity,
            background: 'linear-gradient(90deg, #FF6B6B, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          气候变化：危机与行动
        </h1>
        <p
          style={{
            fontSize: 32,
            opacity: subtitleOpacity,
            color: 'rgba(255, 255, 255, 0.7)',
            margin: 0,
          }}
        >
          Climate Change: Crisis and Action
        </p>
      </div>

      {/* 主要内容区域 - 三列并排 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 30 }}>
        {/* 温度计 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 20, color: '#FF6B6B', fontWeight: 'bold' }}>
            全球平均温度异常 (°C)
          </div>
          <Thermometer
            value={currentDataPoint.tempAnomaly}
            maxValue={1.5}
            frame={frame}
            fps={fps}
          />
        </div>

        {/* CO₂ 浓度 + 极端天气事件 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <div style={{ fontSize: 28, marginBottom: 20, color: '#FFD700', fontWeight: 'bold' }}>
            CO₂ 浓度 (ppm)
          </div>
          <CO2BarChart data={climateData} frame={frame} fps={fps} />

          {/* 极端天气事件 */}
          <div style={{ marginTop: 50, width: '100%' }}>
            <div
              style={{
                fontSize: 32,
                marginBottom: 20,
                color: '#FF8C00',
                fontWeight: 'bold',
                textAlign: 'center',
                opacity: interpolate(frame, [120, 150], [0, 1]),
              }}
            >
              极端天气事件增加 (2010-2024)
            </div>
            <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
              {extremeWeatherEvents.map((event, index) => (
                <WeatherEventCard key={event.type} event={event} index={index} frame={frame} fps={fps} />
              ))}
            </div>
          </div>
        </div>

        {/* 冰盖 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 20, color: '#4FC3F7', fontWeight: 'bold' }}>
            北极冰盖面积 (百万平方公里)
          </div>
          <IceCaps iceExtent={currentDataPoint.iceExtent} frame={frame} fps={fps} />
          <div
            style={{
              marginTop: 20,
              fontSize: 48,
              fontWeight: 'bold',
              color: '#4FC3F7',
            }}
          >
            {currentDataPoint.iceExtent.toFixed(1)} M km²
          </div>
        </div>
      </div>

      {/* 行动号召 */}
      <CallToAction frame={frame} fps={fps} />

      {/* 底部信息 */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          right: 80,
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.4)',
        }}
      >
        数据来源：NASA, NOAA, IPCC
      </div>
    </AbsoluteFill>
  );
};
